import { getTranslationKey } from '@/src/utils/header/headerUtils'
import { Box, Link, keyframes } from '@mui/material'
import React from 'react'

import NewBadge from '@/src/components/common/home/flightSearch/searchForm/NewBadge'

export interface NavLink {
    label: string
    href: string
    labelKey?: string
    icon?: React.ReactNode
    isActive?: boolean
    /** Translation key for a small pill beside the label, e.g. a 'new' flag. */
    badgeKey?: string
}

interface HeaderNavigationProps {
    navLinks: NavLink[]
    variant: 'flyin' | 'cleartrip'
    getNavIcon?: (label: string) => React.ReactNode
    t: (key: string) => string
}

/* A slow breath, not a blink — enough to draw the eye to a newly launched vertical. */
const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.08); }
`

const HeaderNavigation: React.FC<HeaderNavigationProps> = ({ navLinks, variant, getNavIcon, t }) => {
    if (variant === 'cleartrip') {
        return (
            <Box component="nav" sx={{ display: 'flex', gap: 3 }}>
                {navLinks.map(link => (
                    <Link
                        key={link.label}
                        href={link.href}
                        underline="none"
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: link.isActive ? 'primary.main' : 'text.secondary',
                            transition: 'color 0.2s',
                            pb: 0.5,
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            {t(link.label.toLowerCase())}
                            {link.badgeKey && (
                                <NewBadge
                                    label={t(link.badgeKey)}
                                    sx={{
                                        animation: `${pulse} 2s ease-in-out infinite`,
                                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
                                    }}
                                />
                            )}
                        </Box>
                    </Link>
                ))}
            </Box>
        )
    }

    return (
        <Box component="nav" sx={{ display: 'flex', gap: 2.5 }}>
            {navLinks.map((link, index) => (
                <Link
                    key={link.label}
                    href={link.href}
                    underline="none"
                    sx={{
                        position: 'relative',
                        fontSize: 14,
                        fontWeight: link.isActive ? 600 : 500,
                        color: link.isActive ? 'primary.main' : '#1a2a3a',
                        '&:hover': { color: 'primary.dark' },
                        transition: 'color 0.2s',
                        px: 0.7,
                        display: {
                            xs: index < 2 ? 'flex' : 'none',
                            sm: index < 2 ? 'flex' : 'none',
                            md: 'flex'
                        },
                        alignItems: 'center',
                        // Active-tab indicator: a rounded-top bar dropped to the header's bottom edge.
                        ...(link.isActive && {
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                insetInline: 0,
                                bottom: { xs: -14, sm: -17 },
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                                bgcolor: 'primary.main'
                            }
                        })
                    }}
                >
                    {t(getTranslationKey(link.label, link.labelKey))}
                </Link>
            ))}
        </Box>
    )
}

export { HeaderNavigation }
export type { HeaderNavigationProps }
